// src/Components/Modal/DynamicModalForm.jsx
//
// TEK input sistemi: her field tipi kanonik Field (sol ikon + muted label +
// borderless/boxed control) + native control (@/design-system/native) ile
// render edilir. Tarih/saat alanları kanonik DateField/TimeField/DateRangeField
// (@/design-system/components/DateTimeField) kullanır — eski tek-tek-ok time
// stepper popup'ı tamamen kaldırıldı. Bootstrap class'ı (.app-input/.app-label/
// .app-checkbox) ve kendi Row/Col grid'i YOK.
//
// Wiring birebir korunur: localForm/handleChange/onFormChange.
import React, { useEffect, useRef, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  UncontrolledTooltip,
  InputGroup,
  InputGroupText,
  Input,
} from "@/design-system/native";
import Field from "@/design-system/components/Field";
import {
  DateField,
  TimeField,
  DateRangeField,
} from "@/design-system/components/DateTimeField";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { HelpCircle, X, Eye, EyeOff, Key, Paperclip } from "lucide-react";
import { Select } from "@/design-system/components/Select";
import TextareaComposer from "../Common/TextareaComposer";
import styles from "./DynamicModalForm.module.scss";

const buildMultiValue = (value, options = []) => {
  if (!Array.isArray(value)) return [];
  if (!value.length) return [];
  if (typeof value[0] === "object" && value[0] !== null) return value;
  return options.filter((o) => value.includes(o.value ?? o.label));
};

const formatColoredOption = (opt) => (
  <div className={styles.optionRow}>
    {opt.color && (
      <span className={styles.optionDot} style={{ "--dot-color": opt.color }} />
    )}
    <span>{opt.label}</span>
  </div>
);

// react-select stillerini --field-* token'lara çeker (tek input sistemi ile
// tutarlı: aynı kenarlık/zemin/focus-ring her yerde).
const fieldTokenSelectStyles = {
  control: (base, state) => ({
    ...base,
    backgroundColor: "var(--field-bg)",
    borderColor: state.isFocused
      ? "var(--field-border-focus)"
      : "var(--field-border)",
    boxShadow: state.isFocused ? "var(--field-focus-ring)" : "none",
    borderRadius: "var(--field-radius)",
    minHeight: 38,
    "&:hover": { borderColor: "var(--field-border-focus)" },
  }),
  menu: (base) => ({ ...base, zIndex: 1700 }),
  menuList: (base) => ({ ...base, maxHeight: 180 }),
  placeholder: (base) => ({ ...base, color: "var(--field-placeholder)" }),
  input: (base) => ({ ...base, color: "var(--field-text)" }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "var(--field-bg)",
    border: "1px solid var(--field-border)",
    borderRadius: 14,
    paddingInline: 2,
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "var(--field-text)",
    fontWeight: 600,
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: "var(--ds-text-muted)",
    ":hover": { color: "var(--ds-text)", backgroundColor: "transparent" },
  }),
};

const getFieldName = (field = {}) => field.name ?? field.id;

const isFieldVisible = (field = {}, currentForm = {}) =>
  !field.condition || field.condition(currentForm);

const DynamicModalForm = ({
  isOpen,
  toggle,
  title,
  fields = [],
  formData = {},
  onSave = () => {},
  onFormChange = () => {},
  onSaveAndContinue = null,
  onNext = null,
  saveButtonText = "Save",
  saveButtonColor = "primary",
  closeOnSave = true,
  footerLeft = null,
  showFooterUpload = false,
  fileFieldName = "file",
}) => {
  const [localForm, setLocalForm] = useState(formData || {});
  const [passwordVisibility, setPasswordVisibility] = useState({});
  const uploadInputRef = useRef(null);
  const prevOpenRef = useRef(false);

  const stripHiddenFields = (data = {}) => {
    if (!Array.isArray(fields)) return data;
    const cleaned = { ...data };
    fields.forEach((field) => {
      const fieldName = getFieldName(field);
      if (!fieldName) return;
      if (!isFieldVisible(field, data)) {
        delete cleaned[fieldName];
      }
    });
    return cleaned;
  };

  // Form YALNIZCA modal KAPALI→AÇIK geçişinde seed'lenir. ESKİ kod
  // `[formData, isOpen]` deps'iyle her parent re-render'ında (call-site inline
  // `formData={{...}}` → yeni referans) reset ediyor, açıkken KULLANICI GİRİŞİNİ
  // SİLİYORDU (§6 bug). Kök çözüm: açılış geçişini ref ile yakala (call-site
  // useMemo'ya gerek kalmadan merkezi düzeltme).
  useEffect(() => {
    if (isOpen && !prevOpenRef.current) {
      setLocalForm(formData || {});
      setPasswordVisibility({});
    }
    prevOpenRef.current = isOpen;
  }, [isOpen, formData]);

  // ✅ Ortak değişim fonksiyonu
  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    let newValue = value;
    if (type === "checkbox") newValue = checked;
    if (type === "file") newValue = files[0];
    setLocalForm((prev) => {
      const updated = { ...prev, [name]: newValue };
      onFormChange(updated);
      return updated;
    });
  };

  // ✅ Tarih seçimi için özel değişim (Date → YYYY-MM-DD)
  const handleChangeDate = (date, fieldName) => {
    if (!date) {
      setLocalForm((prev) => {
        const updated = { ...prev, [fieldName]: "" };
        onFormChange(updated);
        return updated;
      });
      return;
    }

    const localDate = new Date(
      date.getTime() - date.getTimezoneOffset() * 60000
    )
      .toISOString()
      .split("T")[0];

    setLocalForm((prev) => {
      const updated = { ...prev, [fieldName]: localDate };
      onFormChange(updated);
      return updated;
    });
  };

  // ✅ Saat değişimi (TimeField normalize edilmiş "HH:mm" döner)
  const handleChangeTime = (timeValue, fieldName) => {
    setLocalForm((prev) => {
      const updated = { ...prev, [fieldName]: timeValue };
      onFormChange(updated);
      return updated;
    });
  };

  const handleColorSelect = (color) => {
    setLocalForm((prev) => {
      const updated = { ...prev, color, manualColor: true };
      onFormChange(updated);
      return updated;
    });
  };

  const handleFooterFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setLocalForm((prev) => {
      const updated = { ...prev, [fileFieldName]: file };
      onFormChange(updated);
      return updated;
    });
  };

  const triggerFooterUpload = () => {
    if (uploadInputRef.current) uploadInputRef.current.click();
  };

  const handleSave = () => {
    const cleaned = stripHiddenFields(localForm);
    onSave(cleaned);
    if (closeOnSave) toggle();
  };

  const handleSaveAndContinue = () => {
    if (!onSaveAndContinue) return;
    const cleaned = stripHiddenFields(localForm);
    const nextForm = onSaveAndContinue(cleaned);
    const updated = nextForm || {};
    setLocalForm(updated);
    onFormChange(updated);
  };

  const handleNext = () => {
    if (!onNext) return;
    const cleaned = stripHiddenFields(localForm);
    onNext(cleaned);
  };

  const togglePasswordVisibility = (fieldName) =>
    setPasswordVisibility((prev) => ({ ...prev, [fieldName]: !prev[fieldName] }));

  const generatePassword = () => {
    const charset =
      "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz0123456789!@#$%^&*";
    let pwd = "";
    for (let i = 0; i < 12; i++) {
      pwd += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return pwd;
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="lg"
      backdrop="static" // boşluğa tıklayınca kapanmasın
      keyboard={false}
      zIndex={1500}
      modalClassName="messages-compose-modal"
      contentClassName="messages-compose-modal__content"
      backdropClassName="messages-compose-modal__backdrop"
    >
      <ModalHeader toggle={toggle}>
        <span className={styles.titleText}>{title}</span>
      </ModalHeader>
      <ModalBody className={styles.body}>
        {fields.map((field, idx) => {
          if (!isFieldVisible(field, localForm)) return null;

          const {
            type: fieldType,
            label,
            placeholder,
            options = [],
            name: fieldName = field.id,
            icon = null,
            showToolbar,
            showToolbarOnFocus,
          } = field;

          switch (fieldType) {
            // 🔹 Text
            case "text":
              return (
                <Field key={idx} icon={icon} label={label}>
                  <input
                    type="text"
                    name={fieldName}
                    placeholder={placeholder}
                    value={localForm[fieldName] || ""}
                    onChange={handleChange}
                  />
                </Field>
              );

            // 🔹 Number
            case "number":
              return (
                <Field key={idx} icon={icon} label={label}>
                  <input
                    type="number"
                    name={fieldName}
                    placeholder={placeholder}
                    value={localForm[fieldName] || ""}
                    onChange={handleChange}
                    min={0}
                  />
                </Field>
              );

            // 🔹 Color palette
            case "color-palette":
              return (
                <Field key={idx} icon={icon} label={label} alignTop>
                  <div className={styles.swatchWrap}>
                    {options.map((color, i) => (
                      <div
                        key={i}
                        onClick={() => handleColorSelect(color)}
                        className={styles.paletteSwatch}
                        style={{
                          "--swatch-color": color,
                          "--swatch-border":
                            (localForm.color || formData.color) === color
                              ? "3px solid var(--ds-text)"
                              : "1px solid var(--ds-border)",
                        }}
                      />
                    ))}
                  </div>
                </Field>
              );

            // 🔹 ReactQuill Editör (Description)
            case "editor":
              return (
                <Field key={idx} icon={icon} label={label} alignTop>
                  <div className={styles.fullWidth}>
                    <ReactQuill
                      theme="snow"
                      value={localForm[fieldName] || ""}
                      onChange={(content) =>
                        setLocalForm((prev) => ({
                          ...prev,
                          [fieldName]: content,
                        }))
                      }
                      placeholder={placeholder || "Enter text..."}
                    />
                  </div>
                </Field>
              );

            // 🔹 Textarea
            case "textarea":
              return (
                <Field key={idx} icon={icon} label={label} alignTop>
                  {field.useComposer ? (
                    <div className={styles.fullWidth}>
                      <TextareaComposer
                        placeholder={placeholder || "Type here..."}
                        value={localForm[fieldName] || ""}
                        showToolbar={showToolbar !== false}
                        showToolbarOnFocus={!!showToolbarOnFocus}
                        onChange={(val) => {
                          const updated = { ...localForm, [fieldName]: val };
                          setLocalForm(updated);
                          onFormChange(updated);
                        }}
                        onSubmit={({ text }) => {
                          const updated = { ...localForm, [fieldName]: text };
                          setLocalForm(updated);
                          onFormChange(updated);
                        }}
                        allowFiles={false}
                        allowAudio={false}
                        hideActions
                      />
                    </div>
                  ) : (
                    <textarea
                      name={fieldName}
                      placeholder={placeholder}
                      value={localForm[fieldName] || ""}
                      onChange={handleChange}
                    />
                  )}
                </Field>
              );

            // 🔹 Select
            case "select":
              return (
                <Field key={idx} icon={icon} label={label} variant="boxed">
                  <select
                    name={fieldName}
                    value={localForm[fieldName] || ""}
                    onChange={handleChange}
                  >
                    <option value="">{placeholder || "Select"}</option>
                    {options.map((opt, i) => {
                      if (!opt) return null;
                      if (typeof opt === "string")
                        return (
                          <option key={i} value={opt}>
                            {opt}
                          </option>
                        );
                      return (
                        <option key={i} value={opt.value ?? opt.label ?? ""}>
                          {opt.label ?? opt.value ?? ""}
                        </option>
                      );
                    })}
                  </select>
                </Field>
              );

            // 🔹 Multi Select (labels) — react-select, --field-* token'lı
            case "multiselect": {
              const normalizedOptions = options
                .filter(Boolean)
                .map((opt) =>
                  typeof opt === "string" ? { value: opt, label: opt } : opt
                );
              const value = buildMultiValue(
                localForm[fieldName],
                normalizedOptions
              );
              return (
                <Field key={idx} icon={icon} label={label} alignTop>
                  <div className={styles.fullWidth}>
                    <Select
                      isMulti
                      options={normalizedOptions}
                      value={value}
                      onChange={(selected) =>
                        setLocalForm((prev) => {
                          const updated = {
                            ...prev,
                            [fieldName]: (selected || []).map(
                              (o) => o.value ?? o.label ?? o
                            ),
                          };
                          onFormChange(updated);
                          return updated;
                        })
                      }
                      placeholder={placeholder || "Select"}
                      formatOptionLabel={formatColoredOption}
                      styles={fieldTokenSelectStyles}
                      menuPlacement="auto"
                      menuShouldScrollIntoView={false}
                    />
                  </div>
                </Field>
              );
            }

            // 🔹 Radio
            case "radio":
              return (
                <Field key={idx} icon={icon} label={label} alignTop>
                  <div className={styles.radioGroup}>
                    {options.map((opt) => (
                      <label key={opt.value} className={styles.choiceLabel}>
                        <input
                          type="radio"
                          name={fieldName}
                          value={opt.value}
                          checked={localForm[fieldName] === opt.value}
                          onChange={handleChange}
                        />{" "}
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </Field>
              );

            // 🔹 Checkbox
            case "checkbox": {
              const tooltip = field.info ? (
                <>
                  <HelpCircle
                    id={`${fieldName}-info-${idx}`}
                    size={16}
                    className={`text-muted ${styles.pointer}`}
                  />
                  <UncontrolledTooltip
                    className="reminders-tooltip"
                    placement="right"
                    target={`${fieldName}-info-${idx}`}
                  >
                    {field.info}
                  </UncontrolledTooltip>
                </>
              ) : null;

              return (
                <Field key={idx} icon={icon} label={label}>
                  <label className={styles.choiceLabel}>
                    <input
                      type="checkbox"
                      name={fieldName}
                      checked={localForm[fieldName] || false}
                      onChange={handleChange}
                    />
                    {tooltip}
                  </label>
                </Field>
              );
            }

            // 🔹 File
            case "file":
              if (field.renderInFooter) return null;
              return (
                <Field key={idx} icon={icon} label={label} alignTop>
                  <div className={styles.fullWidth}>
                    <label htmlFor={fieldName} className={styles.uploadLabel}>
                      <Paperclip size={14} className={styles.iconGap} />
                      Upload File
                    </label>
                    <input
                      id={fieldName}
                      type="file"
                      name={fieldName}
                      onChange={handleChange}
                      className={styles.hiddenInput}
                    />
                    {localForm[fieldName] && (
                      <div className={styles.fileNote}>
                        {localForm[fieldName].name}
                      </div>
                    )}
                  </div>
                </Field>
              );

            // 🔹 Date picker (kanonik DateField — sadece tarih)
            case "datepicker":
              return (
                <Field key={idx} icon={icon} label={label}>
                  <div className={styles.pickerWrap}>
                    <DateField
                      dateValue={localForm[fieldName] || ""}
                      onDateChange={(date) => handleChangeDate(date, fieldName)}
                      datePlaceholder={placeholder || "Select date"}
                      onTimeChange={() => {}}
                    />
                  </div>
                </Field>
              );

            // 🔹 Time (kanonik TimeField — yazılabilir + 30-dk dropdown)
            case "time":
              return (
                <Field key={idx} icon={icon} label={label}>
                  <div className={styles.pickerWrap}>
                    <TimeField
                      value={localForm[fieldName] || ""}
                      onChange={(next) => handleChangeTime(next, fieldName)}
                      placeholder={placeholder || "Time"}
                    />
                  </div>
                </Field>
              );

            // 🔹 Date + Time (kanonik DateField — tarih + saat)
            case "datetime":
              return (
                <Field key={idx} icon={icon} label={label}>
                  <div className={styles.pickerWrap}>
                    <DateField
                      dateValue={localForm.date || ""}
                      timeValue={localForm.time || ""}
                      onDateChange={(date) => handleChangeDate(date, "date")}
                      onTimeChange={(next) => handleChangeTime(next, "time")}
                      datePlaceholder="Select date"
                    />
                  </div>
                </Field>
              );

            // 🔹 Composer (toolbar/files/audio)
            case "composer": {
              const isStringOnly = field.stringOnly;
              const value = localForm[fieldName] || (isStringOnly ? "" : {});
              return (
                <Field key={idx} icon={icon} label={label} alignTop>
                  <div className={styles.fullWidth}>
                    <TextareaComposer
                      placeholder={placeholder || "Write a message..."}
                      value={isStringOnly ? value : value.text || ""}
                      onChange={(val) => {
                        const prevVal = localForm[fieldName];
                        const nextValue = isStringOnly
                          ? val
                          : { ...(prevVal || {}), text: val };
                        const isSame = isStringOnly
                          ? prevVal === val
                          : prevVal?.text === val;
                        if (isSame) return;
                        const updated = { ...localForm, [fieldName]: nextValue };
                        onFormChange(updated);
                      }}
                      onSubmit={({ text, files, audios }) => {
                        const payload = isStringOnly
                          ? text
                          : { text, files, audios };
                        const updated = { ...localForm, [fieldName]: payload };
                        onFormChange(updated);
                      }}
                      submitLabel={field.submitLabel || "Save"}
                      allowFiles={field.allowFiles && !isStringOnly}
                      allowAudio={field.allowAudio && !isStringOnly}
                      hideActions
                      showToolbar={field.showToolbar}
                      showToolbarOnFocus={field.showToolbarOnFocus}
                      className={field.className || ""}
                    />
                  </div>
                </Field>
              );
            }

            // 🔹 Password with generate & eye toggle
            case "password-with-actions": {
              const isVisible = passwordVisibility[fieldName];
              const inputType = isVisible ? "text" : "password";
              return (
                <Field key={idx} icon={icon} label={label} variant="boxed">
                  <InputGroup className={styles.fullWidth}>
                    <Input
                      type={inputType}
                      name={fieldName}
                      placeholder={placeholder || "Password"}
                      value={localForm[fieldName] || ""}
                      onChange={handleChange}
                    />
                    <InputGroupText
                      role="button"
                      className={styles.pointer}
                      onClick={() => {
                        const generated = generatePassword();
                        const updated = {
                          ...localForm,
                          [fieldName]: generated,
                        };
                        setLocalForm(updated);
                        onFormChange(updated);
                      }}
                    >
                      <Key size={14} className={styles.iconGap} />
                      Generate
                    </InputGroupText>
                    <InputGroupText
                      role="button"
                      className={styles.pointer}
                      onClick={() => togglePasswordVisibility(fieldName)}
                    >
                      {isVisible ? <Eye size={16} /> : <EyeOff size={16} />}
                    </InputGroupText>
                  </InputGroup>
                </Field>
              );
            }

            // 🔹 Date+Time satırı (kanonik DateField / DateRangeField)
            case "dateTimeRow": {
              const leftDate = field.leftDateName;
              const rightTime = field.rightTimeName;
              const endDate = field.endDateName;
              const endTime = field.endTimeName;
              const hasRange = !!(endDate || endTime);

              return (
                <Field key={idx} icon={icon} label={label} alignTop>
                  <div className={styles.fullWidth}>
                    {hasRange ? (
                      <DateRangeField
                        startLabel={field.startLabel || "Start"}
                        endLabel={field.endLabel || "End"}
                        startDateValue={localForm[leftDate] || ""}
                        startTimeValue={localForm[rightTime] || ""}
                        endDateValue={endDate ? localForm[endDate] || "" : ""}
                        endTimeValue={endTime ? localForm[endTime] || "" : ""}
                        onStartDateChange={(date) =>
                          handleChangeDate(date, leftDate)
                        }
                        onStartTimeChange={(next) =>
                          handleChangeTime(next, rightTime)
                        }
                        onEndDateChange={(date) =>
                          endDate && handleChangeDate(date, endDate)
                        }
                        onEndTimeChange={(next) =>
                          endTime && handleChangeTime(next, endTime)
                        }
                      />
                    ) : (
                      <DateField
                        dateValue={localForm[leftDate] || ""}
                        timeValue={localForm[rightTime] || ""}
                        onDateChange={(date) => handleChangeDate(date, leftDate)}
                        onTimeChange={(next) =>
                          handleChangeTime(next, rightTime)
                        }
                        datePlaceholder={leftDate}
                      />
                    )}
                  </div>
                </Field>
              );
            }

            // 🔹 Custom render
            case "custom":
              return (
                <Field key={idx} icon={icon} label={label} alignTop>
                  <div className={styles.fullWidth}>
                    {field.render({
                      localForm,
                      setLocalForm,
                      onFormChange,
                    })}
                  </div>
                </Field>
              );

            default:
              return null;
          }
        })}
      </ModalBody>

      <ModalFooter>
        <div className={styles.footerLeft}>
          {showFooterUpload && (
            <div className={styles.footerUpload}>
              <input
                ref={uploadInputRef}
                type="file"
                name={fileFieldName}
                onChange={handleFooterFileChange}
                className={styles.hiddenInput}
              />
              <Button
                color="light"
                className={styles.footerUploadBtn}
                onClick={triggerFooterUpload}
              >
                <Paperclip size={16} className={styles.iconGap} />
                Upload File
              </Button>
              {localForm[fileFieldName] && (
                <span className={styles.truncateFileName}>
                  {localForm[fileFieldName].name}
                </span>
              )}
            </div>
          )}
          {footerLeft}
        </div>
        <div className={styles.footerRight}>
          <Button
            className={styles.closeButton}
            color="white"
            onClick={toggle}
          >
            <X size={16} />
            Close
          </Button>
          {onNext && (
            <Button color="primary" outline onClick={handleNext}>
              Next
            </Button>
          )}
          {onSaveAndContinue && (
            <Button color="primary" outline onClick={handleSaveAndContinue}>
              Save & continue
            </Button>
          )}
          <Button color={saveButtonColor} onClick={handleSave}>
            {saveButtonText}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default DynamicModalForm;
